import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Camera, Mail, Phone, MapPin, Shield, Lock, CheckCircle, Award, Clock, UploadCloud, Save } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, authAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.text.secondary};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled(Card)`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 92px;
  height: 92px;
`;

const AvatarImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  letter-spacing: 0.05em;
  text-transform: uppercase;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: ${props => (props.hasImage ? 'block' : 'none')};
  }
`;

const AvatarAction = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: ${props => props.theme.borderRadius.full};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.primary};
  box-shadow: ${props => props.theme.shadows.md};
  cursor: pointer;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const HeroDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 200px;
`;

const HeroName = styled.h2`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const HeroRole = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
`;

const StatusPill = styled.span`
  padding: 0.25rem 0.85rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  backdrop-filter: blur(8px);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const FormCard = styled(Card)`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardHeading = styled.h3`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const Description = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.medium};
  padding: 0.85rem 1rem;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const SecurityForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ToggleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ToggleText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  span {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.fontSize.sm};
  }
`;

const SwitchWrapper = styled.label`
  position: relative;
  display: inline-flex;
  cursor: pointer;
  align-items: center;
`;

const SwitchInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const SwitchVisual = styled.span`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: ${props => (props.checked ? props.theme.colors.primary : props.theme.colors.gray[300])};
  transition: background 0.25s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => (props.checked ? '22px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.2);
    transition: left 0.25s ease;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const InfoIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: ${props => props.theme.typography.fontSize.sm};

  span {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.fontSize.xs};
  }
`;

const Profile = () => {
  const { theme } = useTheme();
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    region: '',
    job_title: '',
    bio: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [preferences, setPreferences] = useState({
    announcements: true,
    weekly_digest: false,
    login_alerts: true,
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        region: user.region || '',
        job_title: user.job_title || '',
        bio: user.bio || '',
      });
      setAvatarPreview(user.profile_photo_url || null);
    }
  }, [user]);

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const getRoleDisplayName = role => {
    const roleNames = {
      system_admin: 'System Administrator',
      regional_coordinator: 'Regional Coordinator',
      data_entry_officer: 'Data Entry Officer',
      program_officer: 'Program Officer',
      member: 'Member',
      super_admin: 'Super Administrator',
    };
    return roleNames[role] || role || 'Member';
  };

  const handleProfileInputChange = event => {
    const { name, value } = event.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordInputChange = event => {
    const { name, value } = event.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarSelect = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
  };

  const handleProfileSubmit = async event => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const payload = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        payload.append(key, value || '');
      });
      if (avatarFile) {
        payload.append('profile_photo', avatarFile);
      }
      const { data } = await usersAPI.updateProfile(payload);
      updateUser(data);
      toast.success('Profile updated successfully');
      setAvatarFile(null);
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to update profile';
      toast.error(message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async event => {
    event.preventDefault();
    setSavingPassword(true);
    try {
      await authAPI.changePassword(passwordForm);
      toast.success('Password changed successfully');
      setPasswordForm({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.errors?.current_password?.[0] || 'Unable to change password';
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const togglePreference = key => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!user) {
    return null;
  }

  return (
    <PageWrapper theme={theme}>
      <PageHeader theme={theme}>
        <PageTitle theme={theme}>My Profile</PageTitle>
        <PageSubtitle theme={theme}>Manage your personal information, security settings, and preferences.</PageSubtitle>
      </PageHeader>

      <HeroCard theme={theme}>
        <HeroContent>
          <AvatarWrapper>
            <AvatarImage hasImage={!!avatarPreview}>
              {avatarPreview ? <img src={avatarPreview} alt="Profile" /> : getUserInitials()}
            </AvatarImage>
            <AvatarAction onClick={() => fileInputRef.current?.click()} title="Change profile picture">
              <Camera size={18} />
            </AvatarAction>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarSelect}
            />
          </AvatarWrapper>

          <HeroDetails>
            <HeroName>{user.first_name} {user.last_name}</HeroName>
            <HeroRole>{getRoleDisplayName(user.role)}</HeroRole>
            <StatusPill>
              <CheckCircle size={14} />
              Active account
            </StatusPill>
          </HeroDetails>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              leftIcon={<UploadCloud size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Photo
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAvatarFile(null);
                setAvatarPreview(user.profile_photo_url || null);
              }}
            >
              Reset
            </Button>
          </div>
        </HeroContent>
      </HeroCard>

      <SectionGrid>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <FormCard>
            <CardTitle>
              <Shield size={18} color={theme.colors.primary} />
              <CardHeading theme={theme}>Account Information</CardHeading>
            </CardTitle>
            <Description theme={theme}>Update your basic profile information and contact details.</Description>
            <form onSubmit={handleProfileSubmit}>
              <FormGrid>
                <Input
                  label="First Name"
                  name="first_name"
                  value={profileForm.first_name}
                  onChange={handleProfileInputChange}
                  required
                />
                <Input
                  label="Last Name"
                  name="last_name"
                  value={profileForm.last_name}
                  onChange={handleProfileInputChange}
                  required
                />
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={profileForm.email}
                  onChange={handleProfileInputChange}
                  required
                />
                <Input
                  label="Phone"
                  name="phone"
                  value={profileForm.phone}
                  onChange={handleProfileInputChange}
                />
                <Input
                  label="Region"
                  name="region"
                  value={profileForm.region}
                  onChange={handleProfileInputChange}
                />
                <Input
                  label="Job Title"
                  name="job_title"
                  value={profileForm.job_title}
                  onChange={handleProfileInputChange}
                />
              </FormGrid>

              <div>
                <label style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>About</label>
                <TextArea
                  name="bio"
                  value={profileForm.bio}
                  onChange={handleProfileInputChange}
                  placeholder="Tell others a little about yourself, your work, or your interests."
                />
              </div>

              <ActionsRow>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setProfileForm({
                      first_name: user.first_name || '',
                      last_name: user.last_name || '',
                      email: user.email || '',
                      phone: user.phone || '',
                      region: user.region || '',
                      job_title: user.job_title || '',
                      bio: user.bio || '',
                    });
                    setAvatarPreview(user.profile_photo_url || null);
                    setAvatarFile(null);
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={savingProfile}
                  leftIcon={<Save size={16} />}
                >
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </ActionsRow>
            </form>
          </FormCard>

          <FormCard>
            <CardTitle>
              <Lock size={18} color={theme.colors.primary} />
              <CardHeading theme={theme}>Security</CardHeading>
            </CardTitle>
            <Description theme={theme}>Update your password to keep your account secure.</Description>
            <SecurityForm onSubmit={handlePasswordSubmit}>
              <Input
                label="Current Password"
                type="password"
                name="current_password"
                value={passwordForm.current_password}
                onChange={handlePasswordInputChange}
                required
              />
              <FormGrid>
                <Input
                  label="New Password"
                  type="password"
                  name="new_password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordInputChange}
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  name="new_password_confirmation"
                  value={passwordForm.new_password_confirmation}
                  onChange={handlePasswordInputChange}
                  required
                />
              </FormGrid>
              <ActionsRow>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={savingPassword}
                >
                  {savingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </ActionsRow>
            </SecurityForm>
          </FormCard>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <FormCard>
            <CardTitle>
              <CheckCircle size={18} color={theme.colors.primary} />
              <CardHeading theme={theme}>Account Activity</CardHeading>
            </CardTitle>
            <InfoList>
              <InfoRow>
                <InfoIcon theme={theme}>
                  <Mail size={18} />
                </InfoIcon>
                <InfoText>
                  {user.email}
                  <span>Email address</span>
                </InfoText>
              </InfoRow>
              <InfoRow>
                <InfoIcon theme={theme}>
                  <Phone size={18} />
                </InfoIcon>
                <InfoText>
                  {user.phone || 'Add a phone number'}
                  <span>Phone</span>
                </InfoText>
              </InfoRow>
              <InfoRow>
                <InfoIcon theme={theme}>
                  <MapPin size={18} />
                </InfoIcon>
                <InfoText>
                  {user.region || 'No region specified'}
                  <span>Region</span>
                </InfoText>
              </InfoRow>
              <InfoRow>
                <InfoIcon theme={theme}>
                  <Clock size={18} />
                </InfoIcon>
                <InfoText>
                  {user.last_login ? format(new Date(user.last_login), 'dd MMM yyyy HH:mm') : 'No login recorded'}
                  <span>Last login</span>
                </InfoText>
              </InfoRow>
            </InfoList>
          </FormCard>

          <FormCard>
            <CardTitle>
              <Award size={18} color={theme.colors.primary} />
              <CardHeading theme={theme}>Notification Preferences</CardHeading>
            </CardTitle>
            <ToggleList>
              <ToggleRow>
                <ToggleText>
                  <strong>Announcements</strong>
                  <span>Product news, feature updates, and release notes.</span>
                </ToggleText>
                <SwitchWrapper>
                  <SwitchInput
                    type="checkbox"
                    checked={preferences.announcements}
                    onChange={() => togglePreference('announcements')}
                  />
                  <SwitchVisual checked={preferences.announcements} />
                </SwitchWrapper>
              </ToggleRow>

              <ToggleRow>
                <ToggleText>
                  <strong>Weekly digest</strong>
                  <span>Summary of new members, applications, and trainings.</span>
                </ToggleText>
                <SwitchWrapper>
                  <SwitchInput
                    type="checkbox"
                    checked={preferences.weekly_digest}
                    onChange={() => togglePreference('weekly_digest')}
                  />
                  <SwitchVisual checked={preferences.weekly_digest} />
                </SwitchWrapper>
              </ToggleRow>

              <ToggleRow>
                <ToggleText>
                  <strong>Login alerts</strong>
                  <span>Be notified of logins from new devices.</span>
                </ToggleText>
                <SwitchWrapper>
                  <SwitchInput
                    type="checkbox"
                    checked={preferences.login_alerts}
                    onChange={() => togglePreference('login_alerts')}
                  />
                  <SwitchVisual checked={preferences.login_alerts} />
                </SwitchWrapper>
              </ToggleRow>
            </ToggleList>
          </FormCard>
        </div>
      </SectionGrid>
    </PageWrapper>
  );
};

export default Profile;
